/**
 * @deprecated Use `ModalHeader` from `components/ui/Modal` for new code. Pass an
 * explicit `onClose` handler instead of routing closes through Redux `modalType`.
 * Migrate existing usages in a follow-up PR.
 */
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import {
  updateMoreOptionShowYouTubeURLModal,
  updateMoreOptionShowAllowedURLModal,
  updateMoreOptionShowChoicesModal,
  updateChoicesShowYouTubeURLModal,
  updateMoreOptionShowImageURLModal,
  initLogQuantityAnswers,
  updateShowLogQuantityModal,
  updateNoteShowNoteModal,
  updateMoreOptionShowCheckListModal,
  updateLogQuantityShowQuestionDetailsModal,
  updateAddToCalendarShowAddToCalendarModalOpened,
  updateFocusSummaryGraphShowFocusSummaryGraphModal,
  updateEisenhowerMatrixIsEisenhowerMatrixModalOpened,
  updateRoutineActivitiesStatsShowRoutineActivitiesStatsModal,
  updateFocusSummaryMore,
  updateFocusSummaryEdit,
  updateShowKeywordModal,
  updateIsLongTermGoalsModalOpened,
  resetMoreOptionAllowedAppsTab
} from 'store/reducer/modal/slice';
import {
  updateIsAccountDeleteConfirmationModalShown,
  updateIsFetchingNextRoutineActivity,
  updateIsRequestDataConfirmationModalShown,
  updateIsShowLibraryMoreOptionsOpened,
  updateIsShowMoreOptionsOpened,
  updateIsShowRoutineModal,
  updateIsUnlockHabitPackModalShown,
  updateShowSkipActivityModal
} from 'store/reducer/setting/slice';
import { MODAL_TYPES, TAKE_NOTE_OPTION } from 'constants/general';
import CloseCircle from 'assets/icons/CloseCircle';
import { ATTRIB } from 'constants/test';
import { updateShowEmbeddedCreateToDoModal } from 'store/reducer/to-do/slice';
import { updateShowProjectTasksModal } from 'store/reducer/platform-integration/slice';
import { updateShowCourseDetailsModal } from 'store/reducer/course/slice';
import { updateShowSurveyDetailModal } from 'store/reducer/admin/slice';
import COLOR from 'constants/color';
import { BaseButtonXs } from 'components/ui/BaseButton';
import { modalSelector } from 'store/reducer/modal/modal.selector';
import { FOCUS_SUMMARY_MODAL_MODE } from 'enums';

interface ModalHeaderProps {
  title?: string;
  modalType?: string;
  style?: string;
  onClose?: () => void;
  subtitle?: string;
}

const ModalHeader = ({
  title,
  modalType,
  style = 'bg-primary-50 dark:bg-secondary-dark-300',
  onClose,
  subtitle
}: ModalHeaderProps) => {
  const dispatch = useAppDispatch();
  const {
    areLogDataValid,
    isCurrentActivityPlaying,
    currentPlayingActivity,
    isShowLibraryMoreOptionsOpened
  } = useAppSelectorShallow(modalSelector);

  const handleOnCloseLogQuantityModal = () => {
    if (!areLogDataValid) {
      dispatch(initLogQuantityAnswers([]));
    }
    dispatch(updateShowLogQuantityModal(false));
    //check next step, note modal or next activity
    if (
      currentPlayingActivity?.take_notes === TAKE_NOTE_OPTION.END_OF_ACTIVITY
    ) {
      dispatch(updateNoteShowNoteModal(true));
    } else {
      dispatch(updateIsFetchingNextRoutineActivity(true));
    }
  };

  const handleClick = () => {
    if (modalType === MODAL_TYPES.ROUTINE) {
      dispatch(updateIsShowRoutineModal(false));
    } else if (modalType === MODAL_TYPES.VIDEOS_URLS) {
      dispatch(updateMoreOptionShowYouTubeURLModal(false));
    } else if (modalType === MODAL_TYPES.ALLOWED_URLS) {
      dispatch(updateMoreOptionShowAllowedURLModal(false));
    } else if (modalType === MODAL_TYPES.CHOICES) {
      dispatch(updateMoreOptionShowChoicesModal(false));
    } else if (modalType === MODAL_TYPES.CHOICE_VIDEO_URLS) {
      dispatch(updateChoicesShowYouTubeURLModal(false));
    } else if (modalType === MODAL_TYPES.IMAGE_URLS) {
      dispatch(updateMoreOptionShowImageURLModal(false));
    } else if (modalType === MODAL_TYPES.UNLOCK_HABIT_PACK) {
      dispatch(updateIsUnlockHabitPackModalShown(false));
    } else if (modalType === MODAL_TYPES.LOG_QUESTION_DETAILS) {
      dispatch(updateLogQuantityShowQuestionDetailsModal(false));
    } else if (modalType === MODAL_TYPES.LOG_QUANTITY) {
      handleOnCloseLogQuantityModal();
    } else if (modalType === MODAL_TYPES.NOTE) {
      dispatch(updateNoteShowNoteModal(false));
      if (!isCurrentActivityPlaying) {
        dispatch(updateIsFetchingNextRoutineActivity(true));
      }
    } else if (modalType === MODAL_TYPES.CHECK_LIST) {
      dispatch(updateMoreOptionShowCheckListModal(false));
    } else if (modalType === MODAL_TYPES.SKIP_ACTIVITY_MODAL) {
      dispatch(updateShowSkipActivityModal(false));
    } else if (modalType === MODAL_TYPES.SET_REMINDER) {
      dispatch(updateAddToCalendarShowAddToCalendarModalOpened(false));
    } else if (modalType === MODAL_TYPES.REQUEST_DATA_CONFIRMATION_MODAL) {
      dispatch(updateIsRequestDataConfirmationModalShown(false));
    } else if (modalType === MODAL_TYPES.ACCOUNT_DELETION_MODAL) {
      dispatch(updateIsAccountDeleteConfirmationModalShown(false));
    } else if (modalType === MODAL_TYPES.FOCUS_SUMMARY_GRAPH) {
      dispatch(updateFocusSummaryGraphShowFocusSummaryGraphModal(false));
    } else if (modalType === MODAL_TYPES.EISENHOWER_MATRIX_MODAL) {
      dispatch(updateEisenhowerMatrixIsEisenhowerMatrixModalOpened(false));
    } else if (modalType === MODAL_TYPES.ROUTINE_ACTIVITIES_STATS_MODAL) {
      dispatch(
        updateRoutineActivitiesStatsShowRoutineActivitiesStatsModal(false)
      );
    } else if (modalType === MODAL_TYPES.EMBEDDED_CREATE_TO_DO_MODAL) {
      dispatch(updateShowEmbeddedCreateToDoModal(false));
    } else if (modalType === MODAL_TYPES.PROJECT_TASKS_MODAL) {
      dispatch(updateShowProjectTasksModal(false));
    } else if (modalType === MODAL_TYPES.FOCUS_SUMMARY_MORE) {
      dispatch(
        updateFocusSummaryMore({
          showFocusSummaryMore: false,
          focusSummary: null
        })
      );
    } else if (modalType === MODAL_TYPES.FOCUS_SUMMARY_EDIT) {
      dispatch(
        updateFocusSummaryEdit({
          showFocusSummaryEdit: false,
          focusSummary: null,
          mode: FOCUS_SUMMARY_MODAL_MODE.EDIT
        })
      );
    } else if (modalType === MODAL_TYPES.UPDATE_KEYWORD_MODAL) {
      dispatch(updateShowKeywordModal(false));
    } else if (modalType === MODAL_TYPES.LONG_TERM_GOALS_MODAL) {
      dispatch(updateIsLongTermGoalsModalOpened(false));
    } else if (modalType === MODAL_TYPES.COURSE_DETAILS_MODAL) {
      dispatch(updateShowCourseDetailsModal(false));
    } else if (modalType === MODAL_TYPES.SURVEY_DETAIL_MODAL) {
      dispatch(updateShowSurveyDetailModal(false));
    } else {
      if (isShowLibraryMoreOptionsOpened) {
        dispatch(updateIsShowLibraryMoreOptionsOpened(false));
      } else {
        dispatch(updateIsShowMoreOptionsOpened(false));
      }
      dispatch(resetMoreOptionAllowedAppsTab());
    }
    onClose?.();
  };

  return (
    <div
      className={`w-full flex justify-between items-center ~px-2.5/4 ~py-2/3 rounded-t-md ${style}`}>
      <div className='flex flex-col w-5/6'>
        <h4
          className='~text-sm/lg font-normal break-words capitalize text-black dark:text-white'
          data-testid='modal-header-title'>
          {title ?? ''}
        </h4>
        {subtitle && (
          <span className='~text-[0.65rem]/xs text-gray-600 dark:text-secondary-200 line-clamp-1'>
            {subtitle}
          </span>
        )}
      </div>
      {!(
        modalType === MODAL_TYPES.INQUIRY ||
        modalType === MODAL_TYPES.REQUEST_USER_DETAILS
      ) && (
        <BaseButtonXs
          icon={
            <CloseCircle
              styles='~w-4/5 h-auto'
              fill={COLOR.PRIMARY.LIGHT_900}
            />
          }
          data-test={ATTRIB.TEST.CLOSE_MODAL}
          onClick={handleClick}
          variant='secondary'
        />
      )}
    </div>
  );
};
export default ModalHeader;
